import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { catchError, EMPTY, filter, map, Observable, startWith, switchMap } from 'rxjs';

/**
 * Initializes AI Chat translations by:
 * 1. Loading only the current language
 * 2. Setting up a subscription to load languages on-demand
 *
 * Uses Angular's inject() to get required dependencies:
 * - HttpClient: For loading translation files
 * - TranslateService: For managing translations
 *
 * Translation files are loaded from '/assets/i18n/ai-chat-{lang}.json'
 * where {lang} is the language code (e.g., 'en-US', 'ja-JP', etc.)
 *
 * * Expected to run in an injection context, otherwise throws an error.
 *
 * @returns void
 */
export function initializeAiChatTranslations(): void {
    const http = inject(HttpClient);
    const translateService = inject(TranslateService);

    const shouldMerge = true;
    const loadedLanguages = new Set<string>();

    const loadTranslationFile = (lang: string): Observable<Record<string, string>> => {
        return http.get<Record<string, string>>(`/assets/i18n/ai-chat-${lang}.json`).pipe(
            catchError(() => {
                console.warn(`AI Chat translations for ${lang} not found.`);
                return EMPTY;
            })
        );
    };

    translateService.onLangChange
        .pipe(
            startWith({ lang: translateService.currentLang || translateService.defaultLang || 'en-US' }),
            map((event) => event.lang),
            filter((lang) => !loadedLanguages.has(lang)),
            switchMap(loadTranslationFile),
            map((translations) => ({ lang: translateService.currentLang, translations }))
        )
        .subscribe(({ lang, translations }) => {
            translateService.setTranslation(lang, translations, shouldMerge);
            loadedLanguages.add(lang);
        });
}
